import React, { useState, useEffect } from 'react';
import SiteCenterInfoForm from './SiteCenterInfoForm';
import SiteAddressInfoForm from './SiteAddressInfoForm';
import { getSiteInfoEditPermissions, resolveAreaZipcodesFormWarehouseId, SitesPageContext } from './siteInfoVisibility';
import DeliveryZoneForm, { DeliveryZoneFormData } from './DeliveryZoneForm';
import AreaZipcodesForm, { AreaZipcodesFormData } from './AreaZipcodesForm';
import ZipcodeConflictModal from './ZipcodeConflictModal';
import { message, Alert, Card } from 'antd';
import ClientCartonBatchInboundChannelsConfig from './ClientCartonBatchInboundChannelsConfig';
import { WarehouseBusinessType } from 'graphql/types/dashboard/SortingDashboardTypes';
import PickupZoneForm, { PickupZoneFormData } from './PickupZoneForm';
import DeliverableZoneForm, { DeliverableZoneFormData } from './DeliverableZoneForm';
import PickupDeadlineTimeForm, { PickupDeadlineTimeFormData } from './PickupDeadlineTimeForm';
import { useMutation, useLazyQuery } from '@apollo/client';
import { GET_WAREHOUSE_BY_ID, GetWarehouseByIdResponse } from '../../../../graphql/queries/warehouseQueries';
import { SAVE_WAREHOUSE_WITH_ZIPCODE_SYNC, UPDATE_WAREHOUSE_ADDRESS, UpdateWarehouseAddressInput, WarehouseMutationResponse } from '../graphql/mutations/warehouseMutations';
import { SAVE_AREAS_WITH_ZIPCODE_SYNC, SaveAreasWithZipcodeSyncResponse, SaveAreasWithZipcodeSyncVariables, AreaTypeInput, CONFIRM_ROUTE_ZIPCODE_CHANGE, ConfirmRouteZipcodeChangeResponse, ConfirmRouteZipcodeChangeVariables } from '../graphql/mutations/areaMutations';
import { GET_ROUTE_ZIPCODE_EDIT_STATUS, GetRouteZipcodeEditStatusResponse, GetRouteZipcodeEditStatusVariables } from '../graphql/queries/getRouteZipcodeEditStatus';
import { WarehouseType } from 'graphql/types/WarehouseType';
import { removeTypename } from 'utils/removeTypename';
import { GET_AREAS_BY_WAREHOUSE_ID, GetAreasByWarehouseIdResponse } from 'graphql/queries/getAreasByWarehouseId';
import { VALIDATE_ZIPCODES_EXIST, ValidateZipcodesExistResponse, ValidateZipcodesExistVariables } from '../graphql/queries/validateZipcodesExist';
import { GET_HUB_DELIVERABLE_ZIPCODES, GetHubDeliverableZipcodesResponse, GetHubDeliverableZipcodesVariables } from '../graphql/queries/hubDeliverableZipcodeQueries';
import { SAVE_HUB_DELIVERABLE_ZIPCODES, SaveHubDeliverableZipcodesResponse, SaveHubDeliverableZipcodesVariables } from '../graphql/mutations/hubDeliverableZipcodeMutations';
import { useTranslation } from 'react-i18next';
import { showRouteChangeErrorModal } from '../utils/routeChangeErrorModal';
import { useZipcodeConflictCheck, GET_ALL_WAREHOUSES_FOR_CONFLICT_CHECK, ZipcodeConflictResult } from '../hooks/useZipcodeConflictCheck';
import { AreaType } from 'graphql/queries/getAreasByWarehouseId';
import { useWarehouseStore } from '../SitesSettingPage';
import { hasHeadquartersAccess, isHeadquartersAdmin, isHubManager } from 'utils/auth';

interface SitesPageBodyProps {
    pageContext?: SitesPageContext;
}

const SitesPageBody: React.FC<SitesPageBodyProps> = ({ pageContext = 'central' }) => {
    const { t } = useTranslation();

    const warehouseId = useWarehouseStore(state => state.warehouseId);

    // no-cache：站点详情只认本次响应，不进 Apollo 归一化缓存。
    // 站点树的 getHubAndWarehousesForSiteManagement 走权限 Dubbo 分支（不查库、字段不全，
    // clientCartonBatchInboundChannelsJson 恒为 null），它与本查询共享 WarehouseType:<id> 实体，
    // 后返回时会把这里已渲染的渠道配置覆盖成 null（表现为首次进页面 Hub 配置开关全灰、刷新后正常）
    const [getWarehouseById, { data, refetch }] = useLazyQuery<GetWarehouseByIdResponse>(GET_WAREHOUSE_BY_ID, {
        fetchPolicy: 'no-cache',
    });
    const [getAreasByWarehouseId, { data: areasData, refetch: refetchAreas }] = useLazyQuery<GetAreasByWarehouseIdResponse>(GET_AREAS_BY_WAREHOUSE_ID);
    const [validateZipcodesExist] = useLazyQuery<ValidateZipcodesExistResponse, ValidateZipcodesExistVariables>(VALIDATE_ZIPCODES_EXIST);
    const [getHubDeliverableZipcodes, { data: deliverableZipcodesData, refetch: refetchDeliverableZipcodes }] = useLazyQuery<GetHubDeliverableZipcodesResponse, GetHubDeliverableZipcodesVariables>(GET_HUB_DELIVERABLE_ZIPCODES);
    const [getRouteZipcodeEditStatus, { data: routeEditStatusData, refetch: refetchRouteEditStatus }] = useLazyQuery<GetRouteZipcodeEditStatusResponse, GetRouteZipcodeEditStatusVariables>(GET_ROUTE_ZIPCODE_EDIT_STATUS, { fetchPolicy: 'network-only' });

    const warehouse = data?.warehouse;
    const areas = areasData?.areaList;
    const routeEditStatus = routeEditStatusData?.getRouteZipcodeEditStatus;
    const isGrayWarehouse = routeEditStatus?.isGrayWarehouse ?? false;
    const routeUpdateInProgress = routeEditStatus?.updateInProgress ?? false;

    useEffect(() => {
        if (warehouseId) {
            getWarehouseById({
                variables: { id: warehouseId },
            });
            getAreasByWarehouseId({
                variables: { warehouseId },
            });
            getRouteZipcodeEditStatus({
                variables: { warehouseId },
            });
        }
    }, [getWarehouseById, getAreasByWarehouseId, getRouteZipcodeEditStatus, warehouseId]);

    // 「更新运单数据中」时轮询编辑状态，直到解锁
    useEffect(() => {
        if (!warehouseId || !routeUpdateInProgress) {
            return;
        }
        const timer = setInterval(() => {
            refetchRouteEditStatus({ warehouseId });
        }, 15000);
        return () => clearInterval(timer);
    }, [warehouseId, routeUpdateInProgress, refetchRouteEditStatus]);

    // 当warehouse数据加载完成且是HUB时，查询可送达邮编列表
    useEffect(() => {
        if (warehouseId && warehouse?.siteType === 1) {
            getHubDeliverableZipcodes({
                variables: { hubId: warehouseId },
            });
        }
    }, [warehouseId, warehouse?.siteType, getHubDeliverableZipcodes]);

    // 当warehouseId变化时，重置所有编辑状态
    useEffect(() => {
        if (warehouseId) {
            setIsWarehouseInfoEditing(false);
            setIsAddressInfoEditing(false);
            setIsDeliveryZoneEditing(false);
            setIsAreaZipcodesEditing(false);
            setIsPickupZoneEditing(false);
            setIsDeliverableZoneEditing(false);
            setIsPickupDeadlineTimeEditing(false);
        }
    }, [warehouseId]);

    // 权限计算
    const isHQ = hasHeadquartersAccess();
    const isHQAdmin = isHeadquartersAdmin();
    const isHubMgr = isHubManager();

    // Edit states
    const [isWarehouseInfoEditing, setIsWarehouseInfoEditing] = useState(false);
    const [isAddressInfoEditing, setIsAddressInfoEditing] = useState(false);
    const [isAddressInfoSaving, setIsAddressInfoSaving] = useState(false);
    const [isDeliveryZoneEditing, setIsDeliveryZoneEditing] = useState(false);
    const [isAreaZipcodesEditing, setIsAreaZipcodesEditing] = useState(false);
    const [isPickupZoneEditing, setIsPickupZoneEditing] = useState(false);
    const [isDeliverableZoneEditing, setIsDeliverableZoneEditing] = useState(false);
    const [isPickupDeadlineTimeEditing, setIsPickupDeadlineTimeEditing] = useState(false);
    const [isHubConfigSaving, setIsHubConfigSaving] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [areaZipcodesData, setAreaZipcodesData] = useState<AreaZipcodesFormData>({
        areas: [],
    });

    // Conflict check states
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [pendingDeliveryZone, setPendingDeliveryZone] = useState<DeliveryZoneFormData | null>(null);
    const [conflictResult, setConflictResult] = useState<ZipcodeConflictResult | null>(null);

    // GraphQL queries and mutations
    const [saveWarehouseMutation] = useMutation<WarehouseMutationResponse>(SAVE_WAREHOUSE_WITH_ZIPCODE_SYNC);
    const [updateWarehouseAddressMutation] = useMutation<WarehouseMutationResponse>(UPDATE_WAREHOUSE_ADDRESS);
    const [saveAreasWithZipcodeSyncMutation] = useMutation<SaveAreasWithZipcodeSyncResponse, SaveAreasWithZipcodeSyncVariables>(SAVE_AREAS_WITH_ZIPCODE_SYNC);
    const [confirmRouteZipcodeChangeMutation] = useMutation<ConfirmRouteZipcodeChangeResponse, ConfirmRouteZipcodeChangeVariables>(CONFIRM_ROUTE_ZIPCODE_CHANGE);
    const { checkConflicts, loading: conflictCheckLoading } = useZipcodeConflictCheck();

    const [invalidZipcodes, setInvalidZipcodes] = useState<string[]>([]);
    const [invalidPickupZipcodes, setInvalidPickupZipcodes] = useState<string[]>([]); // 不存在校验
    const [invalidDeliverableZipcodes, setInvalidDeliverableZipcodes] = useState<string[]>([]); // 可送达区域邮编不存在校验
    const [pickupZoneConflict, setPickupZoneConflict] = useState<{ hubName: string, zipcodes: string[] }[]>([]); // 冲突校验
    const [getAllWarehouses] = useLazyQuery<any>(GET_ALL_WAREHOUSES_FOR_CONFLICT_CHECK);
    const [saveHubDeliverableZipcodesMutation] = useMutation<SaveHubDeliverableZipcodesResponse, SaveHubDeliverableZipcodesVariables>(SAVE_HUB_DELIVERABLE_ZIPCODES);

    /**
     * 过滤对象中的 undefined 和 null 字段，避免 GraphQL 报错
     * 只保留有实际值的字段
     */
    const filterUndefinedAndNull = <T extends Record<string, any>>(obj: T): Partial<T> => {
        const result: any = {};
        Object.keys(obj).forEach(key => {
            if (obj[key] !== undefined && obj[key] !== null) {
                result[key] = obj[key];
            }
        });
        return result;
    };

    /**
     * 保存 warehouse 的通用函数
     * 只发送传入的字段，不再盲目合并整个 warehouse 对象
     * 这样可以避免在保存基础信息时误触发 zipcode 冲突校验
     */
    const saveWarehouse = async (warehouseInfo: Partial<WarehouseType>) => {
        try {
            if (!warehouse?.id) {
                message.error('Missing warehouse ID');
                return;
            }

            // 构造 payload
            const payload: any = {
                id: warehouse.id,
                ...warehouseInfo
            };

            // 🔒 关键修复：确保 zipcode 列表字段始终存在且为数组
            // GraphQL schema 要求这些字段必须存在且不能为 null
            // 如果字段不存在或为 null/undefined，使用原值或空数组
            if (payload.deliveryZoneZipcodeList === null || payload.deliveryZoneZipcodeList === undefined) {
                payload.deliveryZoneZipcodeList = warehouse?.deliveryZoneZipcodeList || [];
            }
            if (payload.pickupZoneZipcodeList === null || payload.pickupZoneZipcodeList === undefined) {
                payload.pickupZoneZipcodeList = warehouse?.pickupZoneZipcodeList || [];
            }
            // deliverableZoneZipcodeList 不在 WarehouseType 中，不需要处理

            await saveWarehouseMutation({
                variables: {
                    warehouse: removeTypename(payload)
                }
            });
            await refetch();
            // 根据 siteType 显示不同的成功提示
            const isHub = warehouse?.siteType === 1;
            if (isHub) {
                message.success(t('central.sites.messages.hubInfoSaved'));
            } else {
                message.success(t('central.sites.messages.warehouseInfoSaved'));
            }
        } catch (error: any) {
            console.error('Failed to save warehouse info:', error);
            // 提取错误消息
            const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to save warehouse information';
            message.error(errorMessage);
        }
    };

    /**
     * 保存"中心信息/仓库信息"（基础信息）
     * 必须包含后端 GraphQL schema 要求的所有字段，包括 zipcode 列表（使用原值）
     * 这样可以确保 GraphQL schema 校验通过，同时不会触发冲突校验（因为使用的是原值）
     */
    const handleSaveWarehouseInfo = async (warehouseInfo: WarehouseType) => {
        if (!warehouse) {
            message.error('Missing warehouse data');
            return;
        }

        // 构造 payload：必需字段（从原 warehouse 获取）+ 可编辑字段（从 warehouseInfo 获取）+ 其他字段（使用原值，避免被清空）
        const payload: any = {
            // 后端 GraphQL schema 要求的必需字段（即使不修改，也必须发送）
            id: warehouse.id,
            name: warehouse.name || '',                     // 必需字段，确保不为 null
            siteType: warehouse.siteType || 1,              // 必需字段
            sortingCode: warehouse.sortingCode || '',       // 必需字段
            addressLine1: warehouse.addressLine1 || '',     // 必需字段，确保不为 null
            parentSiteId: warehouse.parentSiteId || 0,      // 必需字段
            
            // GraphQL schema 要求的 zipcode 列表字段（使用原值，不修改）
            // 必须发送空数组 []，不能是 null 或 undefined
            deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],
            pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],
            
            // 可编辑的字段（从用户输入获取）
            contactName: warehouseInfo.contactName,
            phoneNumber: warehouseInfo.phoneNumber,
            countryCode: warehouseInfo.countryCode || 1,
            extension: warehouseInfo.extension,
            addressLine2: warehouse.addressLine2,
            location: warehouse.location,
            // 后端 Repository.save 会更新所有字段，如果前端不发送，会被设置为 null
            businessType: warehouse.businessType,
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            pickupDeadlineTime: warehouse.pickupDeadlineTime,
            appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        };

        await saveWarehouse(payload);
        setIsWarehouseInfoEditing(false);
    };

    const handleSaveAddressInfo = async (warehouseInfo: WarehouseType) => {
        if (!warehouse?.id) {
            message.error('Missing warehouse data');
            return;
        }

        const latitude = warehouseInfo.location?.latitude;
        const longitude = warehouseInfo.location?.longitude;
        if (latitude == null || longitude == null) {
            message.error(t('central.sites.messages.addressUpdateFailed') as string);
            return;
        }

        const input: UpdateWarehouseAddressInput = {
            id: warehouse.id,
            addressLine1: warehouseInfo.addressLine1 || '',
            addressLine2: warehouseInfo.addressLine2,
            country: warehouseInfo.country,
            state: warehouseInfo.state,
            city: warehouseInfo.city,
            zipcode: warehouseInfo.zipcode,
            location: {
                latitude,
                longitude,
            },
        };

        try {
            setIsAddressInfoSaving(true);
            await updateWarehouseAddressMutation({
                variables: { input: removeTypename(input) },
            });
            await refetch();
            setIsAddressInfoEditing(false);
            message.success(t('central.sites.messages.addressInfoSaved') as string);
        } catch (error: any) {
            const errorMessage =
                error?.graphQLErrors?.[0]?.message ||
                error?.message ||
                (t('central.sites.messages.addressUpdateFailed') as string);
            message.error(errorMessage);
        } finally {
            setIsAddressInfoSaving(false);
        }
    };

    // Handle save delivery zone
    const handleSaveDeliveryZone = async (deliveryZone: DeliveryZoneFormData) => {
        if (!warehouseId || !warehouse || !areas) {
            message.error('Missing warehouse data');
            return;
        }

        // 1. 先校验 zipcodes 是否存在
        const { data } = await validateZipcodesExist({
            variables: { zipcodes: deliveryZone.zipcodes }
        });
        if (data?.validateZipcodesExist && data.validateZipcodesExist.length > 0) {
            setInvalidZipcodes(data.validateZipcodesExist);
            return;
        } else {
            setInvalidZipcodes([]);
        }

        // 2. 再检查冲突
        try {
            const result = await checkConflicts({
                originalZipcodes: warehouse.deliveryZoneZipcodeList || [],
                newZipcodes: deliveryZone.zipcodes,
                currentWarehouseId: warehouseId,
                areas: areas
            });

            if (result.hasAreaConflict || result.hasWarehouseConflict) {
                // 有冲突，显示确认弹窗
                setPendingDeliveryZone(deliveryZone);
                setConflictResult(result);
                setShowConflictModal(true);
            } else {
                // 无冲突，直接保存
                await saveDeliveryZoneDirectly(deliveryZone);
            }
        } catch (error) {
            console.error('Failed to check conflicts:', error);
            message.error('Failed to check conflicts');
        }
    };

    // 直接保存delivery zone（无冲突或用户确认后）
    const saveDeliveryZoneDirectly = async (deliveryZone: DeliveryZoneFormData) => {
        if (!warehouse) return;
        
        // 必须包含所有必需字段 + 要修改的 deliveryZoneZipcodeList + 其他字段（使用原值，避免被清空）
        await saveWarehouse({
            // 后端 GraphQL schema 要求的必需字段（即使不修改，也必须发送）
            id: warehouse.id,
            name: warehouse.name || '',
            siteType: warehouse.siteType || 2,
            sortingCode: warehouse.sortingCode || '',
            addressLine1: warehouse.addressLine1 || '',
            parentSiteId: warehouse.parentSiteId || 0,
            countryCode: warehouse.countryCode || 1,  // 必需字段，确保不为 null
            // 要修改的字段
            deliveryZoneZipcodeList: deliveryZone.zipcodes,  // 要修改的字段
            // GraphQL schema 要求的其他 zipcode 列表字段（使用原值，不修改）
            pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],  // 使用原值
            // 后端 Repository.save 会更新所有字段，如果前端不发送，会被设置为 null
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            contactName: warehouse.contactName,
            phoneNumber: warehouse.phoneNumber,
            extension: warehouse.extension,
            addressLine2: warehouse.addressLine2,
            location: warehouse.location,
            pickupDeadlineTime: warehouse.pickupDeadlineTime,
            appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        });
        setIsDeliveryZoneEditing(false);
    };

    // 处理冲突确认
    const handleConflictConfirm = async () => {
        if (!pendingDeliveryZone || !warehouse || !areas) {
            return;
        }

        try {
            // 1. 保存delivery zone
            await saveDeliveryZoneDirectly(pendingDeliveryZone);

            // 2. 如果有areas冲突，同步更新areas
            if (conflictResult?.hasAreaConflict && conflictResult.conflictingAreas.length > 0) {
                console.log('Updating areas to remove conflicting zipcodes...');

                // 计算被删除的zipcode
                const originalZipcodes = warehouse.deliveryZoneZipcodeList || [];
                const newZipcodes = pendingDeliveryZone.zipcodes;
                const removedZipcodes = originalZipcodes.filter(zipcode => !newZipcodes.includes(zipcode));

                console.log('Removed zipcodes:', removedZipcodes);

                // 更新受影响的areas
                const updatedAreas: AreaTypeInput[] = conflictResult.conflictingAreas.map(
                    (area: AreaType & { conflictingZipcodes: string[] }) => ({
                        id: area.id,
                        name: area.name,
                        sortingCode: area.sortingCode,
                        warehouseId: area.warehouseId,
                        dspId: area.dspId,
                        // 从area的zipcode列表中移除被删除的zipcode
                        zipcodeList: area.zipcodeList.filter((zipcode: string) => !removedZipcodes.includes(zipcode))
                    })
                );

                console.log('Updated areas:', updatedAreas);

                // 保存更新后的areas
                try {
                    await saveAreasWithZipcodeSyncMutation({
                        variables: { areas: updatedAreas }
                    });

                    console.log('Areas updated successfully');
                    message.success('Areas updated to remove conflicting zipcodes');
                } catch (areaError) {
                    console.error('Failed to save areas via API:', areaError);
                    console.log('Areas API might not be available. Areas will be refreshed from server.');

                    // 如果area API不可用，只显示警告，不影响主要功能
                    message.warning('Delivery zone saved, but areas need to be updated manually. Please refresh the page.');
                }
            }

            // 3. 刷新areas数据
            refetchAreas();
        } catch (error) {
            console.error('Failed to update areas:', error);
            message.error('Failed to update areas');
        }

        handleConflictCancel();
    };

    // 取消冲突确认
    const handleConflictCancel = () => {
        setShowConflictModal(false);
        setPendingDeliveryZone(null);
        setConflictResult(null);

        // 如果只有warehouse冲突（不能保存），保持编辑状态
        // 如果有area冲突但用户取消，则退出编辑状态
        if (conflictResult?.hasWarehouseConflict) {
            // 保持编辑状态，让用户修改zipcode
            // setIsDeliveryZoneEditing(true); // 已经是true，无需设置
        } else {
            // 用户取消area冲突的保存，退出编辑状态
            setIsDeliveryZoneEditing(false);
        }
    };

    // Handle save area zipcodes
    const handleSaveAreaZipcodes = async (formData: AreaZipcodesFormData) => {
        try {
            console.log('Saving area zipcodes:', formData);

            // 转换数据格式
            const areasToSave: AreaTypeInput[] = formData.areas.map(area => ({
                id: area.id || undefined,
                name: area.name || `Area ${area.id}`,
                warehouseId: warehouseId?.toString() || '',
                dspId: area.dspId || undefined,
                zipcodeList: area.zipcodeList,
                sortingCode: area.sortingCode || undefined
            }));

            // 调用GraphQL mutation保存
            await saveAreasWithZipcodeSyncMutation({
                variables: { areas: areasToSave }
            });
            message.success(t('central.sites.messages.areaZipcodesSaved'));
            setIsAreaZipcodesEditing(false);

            // 刷新areas数据
            refetchAreas();
        } catch (error: any) {
            console.error('Failed to save Area Zipcodes:', error);

            const graphQLError = error?.graphQLErrors?.[0];
            const messageKey: string | undefined = graphQLError?.extensions?.messageKey;
            const backendMessage: string | undefined =
                graphQLError?.extensions?.message || graphQLError?.message;

            // 针对区域配置校验失败：不展示内部 messageKey，只给用户友好的业务错误
            if (messageKey === 'errors.business.area.validationFailed') {
                message.error(
                    backendMessage ||
                    // 退回到通用友好提示，配合表单上方的红字错误一起使用
                    '路线配置校验失败，请根据上方红色提示修正每一条 Area 后再保存。'
                );
            } else {
                // 其他错误：仍然优先展示后端给出的业务 message，而不是内部 key
                message.error(
                    backendMessage ||
                    error?.message ||
                    'Failed to save Area Zipcodes'
                );
            }
        }
    };

    /**
     * 灰度仓「确认生效」：一次性落库 + 锁定该仓 + 触发下游刷数。
     * 二次确认弹窗与文案由 AreaZipcodesForm 用暂存数据计算后再回调此处。
     */
    const handleConfirmRouteZipcodeChange = async (payload: { areas: AreaTypeInput[]; deletedAreaIds: number[] }) => {
        if (!warehouseId) {
            return;
        }
        try {
            await confirmRouteZipcodeChangeMutation({
                variables: {
                    warehouseId,
                    areas: payload.areas,
                    deletedAreaIds: payload.deletedAreaIds,
                },
            });
            message.success(t('central.sites.messages.routeChangeConfirmed'));
            setIsAreaZipcodesEditing(false);
            refetchAreas();
            refetchRouteEditStatus({ warehouseId });
        } catch (error: any) {
            const graphQLError = error?.graphQLErrors?.[0];
            const backendMessage: string | undefined =
                graphQLError?.extensions?.message || graphQLError?.message;
            // 校验类报错改为常驻弹框展示（用户点关闭才消失，内容可选中/可复制，便于复制在途邮编等）
            showRouteChangeErrorModal(
                backendMessage || error?.message || t('central.sites.messages.routeChangeConfirmFailed'),
                t,
            );
        }
    };

    const handleSavePickupZone = async (pickupZone: PickupZoneFormData) => {
        // 1. 校验 zipcodes 是否存在
        const { data } = await validateZipcodesExist({
            variables: { zipcodes: pickupZone.zipcodes }
        });
        if (data?.validateZipcodesExist && data.validateZipcodesExist.length > 0) {
            setInvalidPickupZipcodes(data.validateZipcodesExist);
            setPickupZoneConflict([]);
            return;
        } else {
            setInvalidPickupZipcodes([]);
        }
        // 2. 校验是否被其他 Hub 使用
        const { data: allWarehousesData } = await getAllWarehouses();
        const currentHubId = warehouse?.id;
        const conflictList: { hubName: string, zipcodes: string[] }[] = [];
        if (allWarehousesData?.warehouses) {
            (allWarehousesData.warehouses as WarehouseType[]).forEach((hub: WarehouseType) => {
                if (hub.id === currentHubId) return;
                const conflictZipcodes = pickupZone.zipcodes.filter(z =>
                    hub.pickupZoneZipcodeList?.includes(z)
                );
                if (conflictZipcodes.length > 0) {
                    conflictList.push({ hubName: hub.name || `Hub ${hub.id}`, zipcodes: conflictZipcodes });
                }
            });
        }
        if (conflictList.length > 0) {
            setPickupZoneConflict(conflictList);
            return;
        } else {
            setPickupZoneConflict([]);
        }
        // 3. 保存
        if (!warehouse) return;
        
        await saveWarehouse({
            // 后端 GraphQL schema 要求的必需字段（即使不修改，也必须发送）
            id: warehouse.id,
            name: warehouse.name || '',
            siteType: warehouse.siteType || 1,
            sortingCode: warehouse.sortingCode || '',
            addressLine1: warehouse.addressLine1 || '',
            parentSiteId: warehouse.parentSiteId || 0,
            countryCode: warehouse.countryCode || 1,  // 必需字段，确保不为 null
            // 要修改的字段
            pickupZoneZipcodeList: pickupZone.zipcodes,  // 要修改的字段
            // GraphQL schema 要求的其他 zipcode 列表字段（使用原值，不修改）
            deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],  // 使用原值
            // 后端 Repository.save 会更新所有字段，如果前端不发送，会被设置为 null
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            contactName: warehouse.contactName,
            phoneNumber: warehouse.phoneNumber,
            extension: warehouse.extension,
            addressLine2: warehouse.addressLine2,
            location: warehouse.location,
            pickupDeadlineTime: warehouse.pickupDeadlineTime,
            appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        });
        setIsPickupZoneEditing(false);
    };

    const handleCancelPickupZone = () => {
        setIsPickupZoneEditing(false);
        setInvalidPickupZipcodes([]);
        setPickupZoneConflict([]);
    };
    const handleSavePickupDeadlineTime = async (deadlineTimeData: PickupDeadlineTimeFormData) => {
        if (!warehouse) return;
        
        // 保存截止时间，必须包含所有必需字段 + zipcode 列表 + 其他字段（使用原值，避免被清空）
        await saveWarehouse({
            // 后端 GraphQL schema 要求的必需字段（即使不修改，也必须发送）
            id: warehouse.id,
            name: warehouse.name || '',
            siteType: warehouse.siteType || 1,
            sortingCode: warehouse.sortingCode || '',
            addressLine1: warehouse.addressLine1 || '',
            parentSiteId: warehouse.parentSiteId || 0,
            countryCode: warehouse.countryCode || 1,  // 必需字段，确保不为 null
            // GraphQL schema 要求的 zipcode 列表字段（使用原值，不修改）
            pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],  // 使用原值
            deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],  // 使用原值
            // 要修改的字段
            pickupDeadlineTime: deadlineTimeData.pickupDeadlineTime,
            appointmentDeadlineTime: deadlineTimeData.appointmentDeadlineTime,
            // 后端 Repository.save 会更新所有字段，如果前端不发送，会被设置为 null
            isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
            clientCartonBatchInboundChannelsJson: warehouse.clientCartonBatchInboundChannelsJson ?? null,
            contactName: warehouse.contactName,
            phoneNumber: warehouse.phoneNumber,
            extension: warehouse.extension,
            addressLine2: warehouse.addressLine2,
            location: warehouse.location,
            timezone: warehouse.timezone,
            country: warehouse.country,
            state: warehouse.state,
            city: warehouse.city,
            zipcode: warehouse.zipcode,
        });
        setIsPickupDeadlineTimeEditing(false);
    };

    const handleCancelPickupDeadlineTime = () => {
        setIsPickupDeadlineTimeEditing(false);
    };

    const handleSaveHubConfig = async (channelsJson: string) => {
        if (!warehouse) return;
        setIsHubConfigSaving(true);
        try {
            await saveWarehouse({
                id: warehouse.id,
                name: warehouse.name || '',
                siteType: warehouse.siteType || 1,
                sortingCode: warehouse.sortingCode || '',
                addressLine1: warehouse.addressLine1 || '',
                parentSiteId: warehouse.parentSiteId || 0,
                countryCode: warehouse.countryCode || 1,
                pickupZoneZipcodeList: warehouse.pickupZoneZipcodeList || [],
                deliveryZoneZipcodeList: warehouse.deliveryZoneZipcodeList || [],
                isClientCartonBatchInboundEnabled: warehouse.isClientCartonBatchInboundEnabled ?? false,
                clientCartonBatchInboundChannelsJson: channelsJson,
                contactName: warehouse.contactName,
                phoneNumber: warehouse.phoneNumber,
                extension: warehouse.extension,
                addressLine2: warehouse.addressLine2,
                location: warehouse.location,
                pickupDeadlineTime: warehouse.pickupDeadlineTime,
                appointmentDeadlineTime: warehouse.appointmentDeadlineTime,
                timezone: warehouse.timezone,
                country: warehouse.country,
                state: warehouse.state,
                city: warehouse.city,
                zipcode: warehouse.zipcode,
            });
        } finally {
            setIsHubConfigSaving(false);
        }
    };

    const handleSaveDeliverableZone = async (deliverableZone: DeliverableZoneFormData) => {
        if (!warehouseId) {
            message.error('Missing hub ID');
            return;
        }

        // 1. 校验 zipcodes 是否存在
        const { data } = await validateZipcodesExist({
            variables: { zipcodes: deliverableZone.zipcodes }
        });
        if (data?.validateZipcodesExist && data.validateZipcodesExist.length > 0) {
            setInvalidDeliverableZipcodes(data.validateZipcodesExist);
            return;
        } else {
            setInvalidDeliverableZipcodes([]);
        }

        // 2. 保存
        try {
            await saveHubDeliverableZipcodesMutation({
                variables: {
                    hubId: warehouseId,
                    input: {
                        zipcodes: deliverableZone.zipcodes
                    }
                }
            });
            message.success(t('central.sites.messages.deliverableZoneSaved'));
            setIsDeliverableZoneEditing(false);
            refetchDeliverableZipcodes();
        } catch (error: any) {
            console.error('Failed to save deliverable zone:', error);
            const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to save deliverable zone';
            message.error(errorMessage);
        }
    };

    const handleCancelDeliverableZone = () => {
        setIsDeliverableZoneEditing(false);
        setInvalidDeliverableZipcodes([]);
    };

    const { isHub, isWarehouse, canEditCenterInfo, canEditAddressInfo } = getSiteInfoEditPermissions({
        pageContext,
        siteType: warehouse?.siteType,
        isHQ,
        isHubMgr,
        isHQAdmin,
    });

    // 基于角色和站点类型的编辑权限
    const canEditHubConfig = isHub && (isHQ || isHubMgr);
    const canEditPickupZone = isHub && isHQ;
    const canEditDeliverableZone = isHub && isHQ;
    const canEditPickupDeadlineTime = isHub && (isHQ || isHubMgr);

    const canEditDeliveryZone = isWarehouse && isHQ;
    const canEditAreaZipcodes = isWarehouse && isHQ;

    const centerInfoTitle = isHub
        ? t('central.sites.details.hubInfo')
        : t('central.sites.details.warehouseInfo');

    // 当站点或权限变化时，确保无权限的编辑态被关闭
    useEffect(() => {
        if (!canEditCenterInfo && isWarehouseInfoEditing) {
            setIsWarehouseInfoEditing(false);
        }
        if (!canEditAddressInfo && isAddressInfoEditing) {
            setIsAddressInfoEditing(false);
        }
        if (!canEditDeliveryZone && isDeliveryZoneEditing) {
            setIsDeliveryZoneEditing(false);
        }
        if (!canEditAreaZipcodes && isAreaZipcodesEditing) {
            setIsAreaZipcodesEditing(false);
        }
        if (!canEditPickupZone && isPickupZoneEditing) {
            setIsPickupZoneEditing(false);
        }
        if (!canEditDeliverableZone && isDeliverableZoneEditing) {
            setIsDeliverableZoneEditing(false);
        }
        if (!canEditPickupDeadlineTime && isPickupDeadlineTimeEditing) {
            setIsPickupDeadlineTimeEditing(false);
        }
    }, [
        canEditCenterInfo,
        canEditAddressInfo,
        canEditDeliveryZone,
        canEditAreaZipcodes,
        canEditPickupZone,
        canEditDeliverableZone,
        canEditPickupDeadlineTime,
        isWarehouseInfoEditing,
        isAddressInfoEditing,
        isDeliveryZoneEditing,
        isAreaZipcodesEditing,
        isPickupZoneEditing,
        isDeliverableZoneEditing,
        isPickupDeadlineTimeEditing
    ]);

    return (
        <div className="flex flex-col gap-4">
            <SiteCenterInfoForm
                data={warehouse || {}}
                isEditing={isWarehouseInfoEditing}
                isNew={false}
                onEdit={canEditCenterInfo ? () => {
                    setIsAddressInfoEditing(false);
                    setIsWarehouseInfoEditing(true);
                } : undefined}
                onSave={handleSaveWarehouseInfo}
                onCancel={() => setIsWarehouseInfoEditing(false)}
                title={centerInfoTitle}
            />

            <SiteAddressInfoForm
                data={warehouse || {}}
                isEditing={isAddressInfoEditing}
                isNew={false}
                onEdit={canEditAddressInfo ? () => {
                    setIsWarehouseInfoEditing(false);
                    setIsAddressInfoEditing(true);
                } : undefined}
                onSave={handleSaveAddressInfo}
                onCancel={() => setIsAddressInfoEditing(false)}
                loading={isAddressInfoSaving}
                title={t('central.sites.details.addressInfo') as string}
            />

            {/* Warehouse-specific forms */}
            {isWarehouse && (
                <>
                    {isDeliveryZoneEditing && invalidZipcodes.length > 0 && (
                        <Alert
                            type="error"
                            message={t('central.sites.messages.invalidZipcodes')}
                            description={invalidZipcodes.join(', ')}
                            showIcon
                            className="mb-4"
                        />
                    )}
                    <DeliveryZoneForm
                        data={{
                            zipcodes: isGrayWarehouse
                                ? Array.from(new Set((areas || []).flatMap(a => a.zipcodeList)))
                                : (warehouse?.deliveryZoneZipcodeList || []),
                        }}
                        isEditing={isGrayWarehouse ? false : isDeliveryZoneEditing}
                        // 灰度仓：派送区域改为只读汇总（路线邮编并集），不提供编辑入口
                        onEdit={isGrayWarehouse ? undefined : (canEditDeliveryZone ? () => setIsDeliveryZoneEditing(true) : undefined)}
                        onSave={handleSaveDeliveryZone}
                        onCancel={() => {
                            setIsDeliveryZoneEditing(false);
                            setInvalidZipcodes([]);
                        }}
                    />

                    <AreaZipcodesForm
                        data={{ areas: areas || [] }}
                        deliveryZoneZipcodes={warehouse?.deliveryZoneZipcodeList || []}
                        warehouseSortingCode={warehouse?.sortingCode}
                        warehouseId={resolveAreaZipcodesFormWarehouseId(warehouse)}
                        warehouseName={warehouse?.name || undefined}
                        isEditing={isAreaZipcodesEditing}
                        isGrayWarehouse={isGrayWarehouse}
                        updateInProgress={routeUpdateInProgress}
                        onChange={setAreaZipcodesData}
                        onEdit={canEditAreaZipcodes ? () => setIsAreaZipcodesEditing(true) : undefined}
                        onSave={handleSaveAreaZipcodes}
                        onConfirmGray={handleConfirmRouteZipcodeChange}
                        onCancel={() => setIsAreaZipcodesEditing(false)}
                        onDataRefresh={refetchAreas}
                    />
                </>
            )}

            {/* Hub-specific forms */}
            {isHub && (
                <>
                    {isPickupZoneEditing && invalidPickupZipcodes.length > 0 && (
                        <Alert
                            type="error"
                            message={t('central.sites.messages.invalidZipcodes')}
                            description={invalidPickupZipcodes.join(', ')}
                            showIcon
                            className="mb-4"
                        />
                    )}
                    {isPickupZoneEditing && pickupZoneConflict.length > 0 && (
                        <Alert
                            type="error"
                            message={t('central.sites.messages.conflictingZipcodes')}   
                            description={pickupZoneConflict.map(c => `${c.hubName}: ${c.zipcodes.join(', ')}`).join('; ')}
                            showIcon
                            className="mb-4"
                        />
                    )}
                    <PickupZoneForm
                        data={{ zipcodes: warehouse?.pickupZoneZipcodeList || [] }}
                        isEditing={isPickupZoneEditing}
                        onEdit={canEditPickupZone ? () => setIsPickupZoneEditing(true) : undefined}
                        onSave={handleSavePickupZone}
                        onCancel={handleCancelPickupZone}
                    />

                    {isDeliverableZoneEditing && invalidDeliverableZipcodes.length > 0 && (
                        <Alert
                            type="error"
                            message={t('central.sites.messages.invalidZipcodes')}
                            description={invalidDeliverableZipcodes.join(', ')}
                            showIcon
                            className="mb-4"
                        />
                    )}
                    <DeliverableZoneForm
                        data={{ zipcodes: deliverableZipcodesData?.getHubDeliverableZipcodes?.zipcodes || [] }}
                        isEditing={isDeliverableZoneEditing}
                        onEdit={canEditDeliverableZone ? () => setIsDeliverableZoneEditing(true) : undefined}
                        onSave={handleSaveDeliverableZone}
                        onCancel={handleCancelDeliverableZone}
                    />

                    <PickupDeadlineTimeForm
                        data={{
                            pickupDeadlineTime: warehouse?.pickupDeadlineTime,
                            appointmentDeadlineTime: warehouse?.appointmentDeadlineTime
                        }}
                        isEditing={isPickupDeadlineTimeEditing}
                        onEdit={canEditPickupDeadlineTime ? () => setIsPickupDeadlineTimeEditing(true) : undefined}
                        onSave={handleSavePickupDeadlineTime}
                        onCancel={handleCancelPickupDeadlineTime}
                    />

                    {/* 大包批量入库为尾程 hub 作业，仅尾程 hub 展示，与 PDA 扫描入口口径一致 */}
                    {warehouse?.businessType === WarehouseBusinessType.LAST_MILE && (
                        <Card
                            className="w-full"
                            variant="borderless"
                            title={
                                <div className="flex items-center">
                                    <span className="w-1 h-6 bg-purple-600 mr-2"></span>
                                    <span>{t('central.sites.details.hubConfig')}</span>
                                </div>
                            }
                        >
                            <ClientCartonBatchInboundChannelsConfig
                                channelsJson={warehouse?.clientCartonBatchInboundChannelsJson}
                                disabled={!canEditHubConfig}
                                saving={isHubConfigSaving}
                                onSave={handleSaveHubConfig}
                            />
                        </Card>
                    )}
                </>
            )}

            {/* Conflict confirmation modal */}
            {conflictResult && (
                <ZipcodeConflictModal
                    visible={showConflictModal}
                    conflictResult={conflictResult}
                    onConfirm={handleConflictConfirm}
                    onCancel={handleConflictCancel}
                    loading={conflictCheckLoading}
                />
            )}
        </div>
    );
};

export default SitesPageBody;

